/**
 * Consulting - Dashboard Page
 * Overview metrics for consulting firms
 */

import { useEffect, useMemo } from 'react';
import { useIndustryProjects } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Briefcase, TrendingUp, DollarSign, FolderOpen, BarChart3, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MainLayout } from '@/components/MainLayout';

export default function ConsultingDashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard de Consultorias | Controle Financeiro';
  }, []);

  const { projects, isLoading } = useIndustryProjects({ domain: CompanyType.CONSULTING });

  const stats = useMemo(() => {
    if (!projects) return null;

    const activeProjects = projects.filter((p) => p.status === 'in_progress').length;
    const completedProjects = projects.filter((p) => p.status === 'completed').length;
    const plannedProjects = projects.filter((p) => p.status === 'planned').length;

    const totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
    const totalActualCost = projects.reduce((sum, p) => sum + (p.actual_cost || 0), 0);
    const totalProfit = totalBudget - totalActualCost;

    const avgBudget = projects.length > 0 ? totalBudget / projects.length : 0;

    return {
      totalProjects: projects.length,
      activeProjects,
      completedProjects,
      plannedProjects,
      totalBudget,
      totalActualCost,
      totalProfit,
      avgBudget,
      profitMargin: totalBudget > 0 ? (totalProfit / totalBudget) * 100 : 0,
    };
  }, [projects]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Briefcase className="h-8 w-8" />
            Dashboard de Consultorias
          </h1>
          <p className="text-muted-foreground">Visão geral dos seus projetos de consultoria</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Total de Projetos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalProjects || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.activeProjects || 0} em andamento · {stats?.plannedProjects || 0} planejados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Orçamento Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.totalBudget || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Valor total contratado</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Margem de Lucro
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.profitMargin.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Lucro: {formatCurrency(stats?.totalProfit || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Valor Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.avgBudget || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Orçamento médio por projeto</p>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Receita Prevista vs Custos
          </CardTitle>
          <CardDescription>Análise financeira consolidada</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Receita Prevista</span>
                <span className="text-2xl font-bold text-green-600">{formatCurrency(stats?.totalBudget || 0)}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Custos Realizados</span>
                <span className="text-2xl font-bold text-orange-600">{formatCurrency(stats?.totalActualCost || 0)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Projetos</CardTitle>
            <CardDescription>Gerencie seu portfólio de consultorias</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/consulting/projects">
              <Button className="w-full">
                <FolderOpen className="mr-2 h-4 w-4" />
                Gerenciar Projetos
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contratos</CardTitle>
            <CardDescription>Gestão de contratos de consultoria</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/consulting/contracts">
              <Button className="w-full" variant="outline">
                <Briefcase className="mr-2 h-4 w-4" />
                Ver Contratos
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Relatórios</CardTitle>
            <CardDescription>Análise financeira e lucratividade</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/consulting/profitability">
              <Button className="w-full" variant="outline">
                <BarChart3 className="mr-2 h-4 w-4" />
                Ver Relatório
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
      </div>
    </MainLayout>
  );
}
